import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";
import { ptBR } from "date-fns/locale";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { useConfig } from "@/store/useConfig";

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  markedDates?: Date[];
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  markedDates = [],
  ...props
}: CalendarProps) {
  const config = useConfig();
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-4 bg-gray-900 text-white", className)}
      locale={ptBR}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        caption: "flex justify-center pt-2 relative items-center",
        caption_label: "text-sm font-medium",
        nav: "space-x-2 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-8 w-8 bg-transparent p-0 opacity-70 hover:opacity-100 transition-opacity"
        ),
        nav_button_previous: "absolute left-2",
        nav_button_next: "absolute right-2",
        table: "w-full border-collapse space-y-2",
        head_row: "flex",
        head_cell: "text-gray-400 rounded-md w-10 font-normal text-[0.9rem]",
        row: "flex w-full mt-2",
        cell: "h-10 w-10 text-center text-sm p-0 relative [&:has([aria-selected])]:bg-gray-800/30 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "h-10 w-10 p-0 font-normal aria-selected:opacity-100 text-white hover:bg-gray-800/50 rounded-full transition-colors"
        ),
        day_range_end: "day-range-end",
        day_selected:
          "bg-gray-700 text-white hover:bg-gray-600 hover:text-white focus:bg-gray-700 focus:text-white",
        day_today: "bg-gray-600 text-white font-bold",
        day_outside: "text-gray-500 opacity-50 hover:bg-gray-800/30",
        day_disabled: "text-gray-600 opacity-50",
        day_range_middle:
          "aria-selected:bg-gray-800/30 aria-selected:text-white",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: () => <ChevronLeft className="h-5 w-5" />,
        IconRight: () => <ChevronRight className="h-5 w-5" />,
      }}
      formatters={{
        formatWeekdayName: (weekday) =>
          // @ts-ignore
          ptBR.localize?.day(weekday, { width: "short" })?.toUpperCase() ?? "",
      }}
      modifiers={{
        marked: markedDates,
      }}
      modifiersStyles={{
        marked: {
          color: config?.settings?.color_secondary,
          backgroundColor: config?.settings?.color_primary,
        },
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
